import { useCallback, useRef, useState } from "react";
import { selectMessageRunSettings, useChatStore } from "@/stores/useChatStore";
import { useAgentSessionContext } from "@/components/chat/AgentSessionContext";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import { ChatSendConflictError, ChatSendValidationError, postChatMessage } from "@/lib/api";
import { newMessageId, newRequestId } from "@/lib/id-generator";
import { nextMessageOrder, type ContentBlock } from "@/lib/chat";
import { findPendingHITL } from "./hitl";

/**
 * HITL ツール結果を送信する共通フック。SegmentationPanel と UserChoicePrompt が共有する。
 *
 * 楽観的に tool_result メッセージを追加してツールカードを completed に遷移させ、追記を
 * POST してから HITL を閉じ、tool_result 以降からストリームを張り直してエージェント応答を
 * 受け取る。ストリームを張り直さないと「考察中」のまま無音で固まる。POST が失敗した場合は
 * 楽観追加を取り消し、未応答へ戻った HITL からパネルを復元して再試行できるようにする。返した callback を
 * 呼ぶ時点のストア状態 (chatId / hitlState) を参照するため、呼び出し側でガードする必要はない。
 * 送信中は submitting が true になり、その間の再送信は無視する (連打で二重に POST しない)。
 * 送信が受理されたら true、送らずに終えたら false を返す。
 */
export function useSubmitHITLToolResult() {
	const { startStreaming } = useAgentSessionContext();
	const [submitting, setSubmitting] = useState(false);
	const submittingRef = useRef(false);

	const submit = useCallback(
		async (result: unknown): Promise<boolean> => {
			if (submittingRef.current) return false;
			const { chatId, hitlState, status } = useChatStore.getState();
			if (!chatId || !hitlState) return false;
			// 停止処理中は回答を送らない (停止が確定するまで待つ)。
			if (status === "stopping") return false;

			const content: ContentBlock[] = [
				{
					type: "tool_result",
					tool_use_id: hitlState.toolUseId,
					content: [{ type: "text", text: JSON.stringify(result) }],
				},
			];

			// clientRequestId は楽観追加の前に採番して DTO に載せておき、前ターンのストリームが
			// サーバ版を配信した際に同じ clientRequestId で楽観メッセージへ詰め替えられる
			// (二重表示を防ぐ) ようにする。
			const optimisticId = newMessageId();
			const messages = useChatStore.getState().messages;
			const clientRequestId = newRequestId();
			useChatStore.getState().addMessage({
				id: optimisticId,
				chatId,
				order: nextMessageOrder(messages),
				role: "user",
				content,
				createdAt: new Date().toISOString(),
				createdById: useCurrentUserStore.getState().id,
				stopReason: null,
				stopDetails: null,
				clientRequestId,
			});

			const expectedRevision = useChatStore.getState().revision;

			// POST を先行させ、受理されてから HITL を閉じてストリームを張る。
			submittingRef.current = true;
			setSubmitting(true);
			try {
				const res = await postChatMessage(chatId, {
					clientRequestId,
					expectedRevision,
					content,
					...selectMessageRunSettings(useChatStore.getState()),
				});
				useChatStore.getState().setChatRevision(res.revision);
				useChatStore.getState().clearHITL();
				startStreaming(chatId);
				return true;
			} catch (err: unknown) {
				const store = useChatStore.getState();
				store.removeMessage(optimisticId);
				store.setStatus("idle");
				// 楽観追加を取り消すと HITL は未応答へ戻る。パネルはこの間に届いたイベントで
				// 畳まれている場合があり (processEvent は messages から未応答 HITL を判定する)、
				// 復元しないと回答手段が消えたまま以降の送信が全て 409 になる。
				const pending = findPendingHITL(useChatStore.getState().messages);
				if (pending) store.setHITL(pending);
				if (err instanceof ChatSendConflictError) {
					if (err.revision !== null) store.setChatRevision(err.revision);
					useToastStore.getState().show({ type: "error", message: err.message });
				} else if (err instanceof ChatSendValidationError) {
					useToastStore.getState().show({ type: "error", message: err.message });
				} else {
					useToastStore.getState().show({
						type: "error",
						message: "結果の送信に失敗しました。しばらくしてから再度お試しください。",
					});
				}
				throw err;
			} finally {
				submittingRef.current = false;
				setSubmitting(false);
			}
		},
		[startStreaming],
	);

	return { submit, submitting };
}
